import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { loginSchema, type LoginFormValues } from '@/utils/schemas'
import { signInWithPassword } from '@/services/authService'
import { Logo } from '@/components/Logo'

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({ resolver: zodResolver(loginSchema) })

  const mutation = useMutation({
    mutationFn: ({ email, password }: LoginFormValues) => signInWithPassword(email, password),
    onSuccess: () => navigate('/'),
  })

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-night to-deep p-14 md:flex">
        <Logo />
        <svg
          viewBox="0 0 400 300"
          className="absolute top-10 right-[-40px] w-[420px] opacity-55"
          aria-hidden="true"
        >
          <g stroke="var(--color-brass)" strokeWidth="1" fill="none" opacity=".5">
            <path d="M300 40 L340 80 L380 60 L360 130 L300 120 L280 170" />
            <path d="M340 80 L320 120 L360 130" />
          </g>
          <g fill="var(--color-ivory)">
            {[
              [300, 40],
              [340, 80],
              [380, 60],
              [360, 130],
              [300, 120],
              [280, 170],
              [320, 120],
            ].map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 3 : 2} />
            ))}
          </g>
          <circle cx="360" cy="130" r="4.5" fill="var(--color-brass)" />
        </svg>
        <div className="relative z-10">
          <h1 className="max-w-[14ch] font-display text-4xl leading-tight text-ivory">
            {t('auth.tagline')}
          </h1>
          <p className="mt-4 max-w-[26ch] font-display text-lg text-ivory-2 italic">
            {t('auth.subtagline')}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-ivory p-14">
        <div className="w-full max-w-sm">
          <div className="mb-2.5 font-mono text-xs tracking-[0.2em] text-brass-ink uppercase">
            {t('auth.eyebrow')}
          </div>
          <h2 className="mb-7 font-display text-3xl text-night">{t('auth.loginTitle')}</h2>

          <form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit((values) => mutation.mutate(values))}
            noValidate
          >
            <div>
              <label htmlFor="email" className="mb-1.5 block font-sans text-[12.5px] text-night">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                className="w-full rounded border border-ivory-2 bg-white px-3.5 py-2.5 text-night outline-none focus:border-brass"
                {...register('email')}
              />
              {errors.email && (
                <p role="alert" className="mt-1 text-sm text-warm-ink">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block font-sans text-[12.5px] text-night">
                {t('auth.password')}
              </label>
              <input
                id="password"
                type="password"
                className="w-full rounded border border-ivory-2 bg-white px-3.5 py-2.5 text-night outline-none focus:border-brass"
                {...register('password')}
              />
              {errors.password && (
                <p role="alert" className="mt-1 text-sm text-warm-ink">
                  {errors.password.message}
                </p>
              )}
            </div>

            {mutation.isError && (
              <p role="alert" className="text-sm text-warm-ink">
                {t('auth.error')}
              </p>
            )}

            <button
              type="submit"
              disabled={mutation.isPending}
              className="mt-1 rounded border border-brass bg-brass px-4 py-2.5 font-sans text-sm font-medium text-night disabled:opacity-50"
            >
              {t('auth.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
